"use client";

import { useEffect } from "react";
import {
  addDiagnosticTools,
  logPointerEventsDiagnostic,
  fixPointerEvents,
  emergencyFix,
} from "@/lib/debug/pointer-events-diagnostic";

interface PointerEventsDiagnosticProps {
  enabled?: boolean;
  autoFix?: boolean;
  keyboardShortcuts?: boolean;
}

/**
 * Debug component that provides pointer events diagnostic tools
 * Only use in development or when debugging pointer events issues
 */
export function PointerEventsDiagnostic({
  enabled = false, // Disabled for now - change to process.env.NODE_ENV === "development" to re-enable
  autoFix = false,
  keyboardShortcuts = true,
}: PointerEventsDiagnosticProps) {
  useEffect(() => {
    if (!enabled) return;

    // Add diagnostic tools to window for console debugging
    addDiagnosticTools();

    // Set up keyboard shortcuts for debugging
    if (keyboardShortcuts) {
      const handleKeyPress = (event: KeyboardEvent) => {
        // Ctrl+Shift+D = Diagnose
        if (event.ctrlKey && event.shiftKey && event.key === "D") {
          event.preventDefault();
          console.log("🔍 Running pointer events diagnostic...");
          logPointerEventsDiagnostic();
        }

        // Ctrl+Shift+F = Fix
        if (event.ctrlKey && event.shiftKey && event.key === "F") {
          event.preventDefault();
          console.log("🔧 Running pointer events fix...");
          const fixed = fixPointerEvents();
          if (fixed) {
            console.log("✅ Issues found and fixed");
          } else {
            console.log("ℹ️ No issues detected");
          }
        }

        // Ctrl+Shift+E = Emergency Fix
        if (event.ctrlKey && event.shiftKey && event.key === "E") {
          event.preventDefault();
          console.log("🚨 Running emergency fix...");
          emergencyFix();
        }
      };

      document.addEventListener("keydown", handleKeyPress);

      // Log available shortcuts
      console.log("⌨️ Pointer Events Debug Shortcuts:");
      console.log("  Ctrl+Shift+D = Diagnose pointer events");
      console.log("  Ctrl+Shift+F = Fix pointer events");
      console.log("  Ctrl+Shift+E = Emergency fix");

      return () => {
        document.removeEventListener("keydown", handleKeyPress);
      };
    }

    // Auto-fix if enabled
    if (autoFix) {
      const interval = setInterval(() => {
        const needsFix =
          document.body.style.pointerEvents === "none" ||
          document.querySelectorAll(
            '[data-radix-dialog-overlay][data-state="closed"]',
          ).length > 0;

        if (needsFix) {
          console.log("🔄 Auto-fixing pointer events issue...");
          fixPointerEvents();
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [enabled, autoFix, keyboardShortcuts]);

  // Don't render anything in production or when disabled
  if (!enabled) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 10,
        right: 10,
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.8)",
        color: "white",
        padding: "8px 12px",
        borderRadius: "4px",
        fontSize: "12px",
        fontFamily: "monospace",
        pointerEvents: "none",
        opacity: 0.7,
      }}
    >
      🛠️ Debug Mode
      <br />
      Ctrl+Shift+D/F/E
    </div>
  );
}

/**
 * Hook to manually trigger pointer events diagnostic
 */
export function usePointerEventsDiagnostic() {
  return {
    diagnose: logPointerEventsDiagnostic,
    fix: fixPointerEvents,
    emergencyFix: emergencyFix,
  };
}
